import { rpx } from 'client/lib/rpx-client';
import { useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { showError } from '@components/app-error';

type Member = Awaited<
  ReturnType<typeof rpx.engagementRetention.getMemberSignals>
>['members'][number];

type OutcomeValue =
  | 'pending'
  | 'saved'
  | 'paused'
  | 'won_back'
  | 'lost_price'
  | 'lost_fit'
  | 'lost_time'
  | 'lost_other'
  | 'no_response'
  | 'replied'
  | 'attended_call'
  | 'other';

type ActionTypeValue =
  | 'note'
  | 'call'
  | 'email_sent'
  | 'pause_offered'
  | 'booking_link_sent'
  | 'personal_invite'
  | 'check_in'
  | 'shoutout'
  | 'defer';

type Source = 'ari_card' | 'barb_segment' | 'auto' | 'manual';

type Kind = 'outcome' | 'note' | 'pause' | 'booking' | 'shoutout';

const OUTCOME_OPTIONS: { value: OutcomeValue; label: string }[] = [
  { value: 'pending', label: 'Pending — no outcome yet' },
  { value: 'saved', label: 'Saved (kept the sub)' },
  { value: 'paused', label: 'Paused' },
  { value: 'won_back', label: 'Won back (after cancel)' },
  { value: 'lost_price', label: 'Lost — price' },
  { value: 'lost_fit', label: 'Lost — fit' },
  { value: 'lost_time', label: 'Lost — time / life' },
  { value: 'lost_other', label: 'Lost — other' },
  { value: 'no_response', label: 'No response' },
  { value: 'replied', label: 'Replied' },
  { value: 'attended_call', label: 'Attended a call' },
  { value: 'other', label: 'Other' },
];

function defaultActionType(kind: Kind): ActionTypeValue {
  switch (kind) {
    case 'pause':
      return 'pause_offered';
    case 'booking':
      return 'booking_link_sent';
    case 'shoutout':
      return 'shoutout';
    case 'note':
      return 'note';
    case 'outcome':
      return 'call';
  }
}

export function OutreachActionDrawer(props: {
  member: Member;
  source: Source;
  kind: Kind;
  onClose: () => void;
  onLogged: (opts?: { keepOpen?: boolean }) => void;
}) {
  const [actionType, setActionType] = useState<ActionTypeValue>(defaultActionType(props.kind));
  const [outcome, setOutcome] = useState<OutcomeValue>('pending');
  const [body, setBody] = useState('');
  const [mrrImpactDollars, setMrrImpactDollars] = useState<string>(
    props.member.mrrCents !== undefined ? `${(props.member.mrrCents / 100).toFixed(0)}` : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tagFeedback, setTagFeedback] = useState<string | undefined>(undefined);

  async function submit() {
    setSubmitting(true);
    setTagFeedback(undefined);
    try {
      const mrrImpactCents = mrrImpactDollars
        ? Math.round(parseFloat(mrrImpactDollars) * 100)
        : undefined;
      const result = await rpx.engagementRetention.logOutreach({
        memberUserId: props.member.userId,
        source: props.source,
        actionType,
        outcome: outcome === 'pending' ? undefined : outcome,
        body: body.trim() ? body.trim() : undefined,
        mrrImpactCents,
      });
      const hasOpFeedback = result.opTags.applied.length > 0 || result.opTags.attempted.length > 0;
      if (result.opTags.applied.length > 0) {
        setTagFeedback(`Applied OP tags: ${result.opTags.applied.join(', ')}`);
      } else if (result.opTags.attempted.length > 0) {
        setTagFeedback(`OP tag attempt failed: ${result.opTags.error || 'unknown error'}`);
      }
      setSubmitted(true);
      if (hasOpFeedback) {
        props.onLogged({ keepOpen: true });
      } else {
        props.onLogged();
      }
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div class="fixed inset-0 z-[1000] flex justify-end">
      <div class="absolute inset-0 bg-black/30" onClick={props.onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Log outreach"
        class="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl"
      >
        <header class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 class="text-base font-semibold text-gray-900">
            Log outreach for {props.member.name || props.member.email}
          </h2>
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-800"
            onClick={props.onClose}
          >
            Close
          </button>
        </header>

        <div class="flex flex-col gap-4 p-5">
          <Field label="What did you do?">
            <select
              class="ruz-input"
              value={actionType}
              onChange={(e) =>
                setActionType((e.target as HTMLSelectElement).value as ActionTypeValue)
              }
            >
              <option value="note">Note (no contact yet)</option>
              <option value="call">Phone call</option>
              <option value="email_sent">Email sent</option>
              <option value="pause_offered">Pause offered</option>
              <option value="booking_link_sent">Booking link sent</option>
              <option value="personal_invite">Personal invite</option>
              <option value="check_in">Check-in message</option>
              <option value="shoutout">Shoutout</option>
              <option value="defer">Defer for later</option>
            </select>
          </Field>

          <Field label="Outcome (if known)">
            <select
              class="ruz-input"
              value={outcome}
              onChange={(e) => setOutcome((e.target as HTMLSelectElement).value as OutcomeValue)}
            >
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="MRR impact (USD)"
            hint="Roughly the monthly revenue saved or lost. Leave blank to skip."
          >
            <input
              type="number"
              class="ruz-input"
              value={mrrImpactDollars}
              onInput={(e) => setMrrImpactDollars((e.target as HTMLInputElement).value)}
              step="1"
              min="0"
            />
          </Field>

          <Field label="Notes" hint="Free-form. What happened, what you said, what they said.">
            <textarea
              class="ruz-input min-h-[120px]"
              value={body}
              onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
              placeholder="What happened on this touch?"
            />
          </Field>

          {tagFeedback && <p class="rounded bg-gray-50 p-2 text-xs text-gray-700">{tagFeedback}</p>}
          {submitted && !tagFeedback && (
            <p class="rounded bg-gray-50 p-2 text-xs text-gray-700">Outreach logged.</p>
          )}

          <div class="flex items-center justify-end gap-2">
            {!submitted && (
              <button
                type="button"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={props.onClose}
                disabled={submitting}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              onClick={submitted ? props.onClose : submit}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : submitted ? 'Done' : 'Log outreach'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field(props: { label: string; hint?: string; children: preact.ComponentChildren }) {
  return (
    <label class="flex flex-col gap-1">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-600">{props.label}</span>
      {props.children}
      {props.hint && <span class="text-xs text-gray-500">{props.hint}</span>}
    </label>
  );
}
